import { useEffect, useState } from "react";
import { Menu, X, Instagram } from "lucide-react";
import { Link } from "react-router-dom";

const SECTIONS = [
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "gallery", label: "Gallery" },
  { id: "process", label: "Process" },
  { id: "planning", label: "Packages" },
  { id: "testimonials", label: "Reviews" },
  { id: "contact", label: "Contact" },
];

export default function Nav({ brand, instagramUrl }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = !scrolled;
  const textColor = dark ? "text-ivory" : "text-charcoal";
  const subText = dark ? "text-ivory/85" : "text-charcoal/80";
  const logo = brand?.logo_url;

  // Cloudinary on-the-fly transparency + colour shift for dark backgrounds.
  // Step 1: e_make_transparent:60 → makes white background transparent.
  // Step 2: e_colorize:100,co_rgb:F7F5F1 → recolour remaining artwork to ivory.
  // (Steps must be separated by '/' so Cloudinary chains them in sequence.)
  const isCloudinary = typeof logo === "string" && logo.includes("res.cloudinary.com");
  const logoLight = logo;
  const logoDark = isCloudinary
    ? logo.replace(
        "/image/upload/",
        "/image/upload/e_make_transparent:60/e_colorize:100,co_rgb:F7F5F1/"
      )
    : null;

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ivory/90 backdrop-blur-xl border-b border-beige" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="inline-flex items-center gap-3 leading-none">
          {logo ? (
            <img
              src={dark && logoDark ? logoDark : logoLight}
              alt={brand?.name || "Weddings by Mostash"}
              className="h-10 md:h-12 w-auto"
            />
          ) : (
            <span className={`font-display text-lg md:text-2xl tracking-tight ${textColor} hover:text-gold transition-colors`}>
              {brand?.name || "Weddings by Mostash"}
            </span>
          )}
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`nav-link-${s.id}`}
              className={`text-[11px] uppercase tracking-[0.22em] font-medium ${subText} hover:text-gold transition-colors`}
            >
              {s.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {instagramUrl ? (
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="nav-instagram"
              className={`hidden sm:inline-flex items-center justify-center h-10 w-10 rounded-full border ${dark ? "border-ivory/40 text-ivory hover:bg-ivory hover:text-charcoal" : "border-charcoal/25 text-charcoal hover:bg-charcoal hover:text-ivory"} transition-all`}
            >
              <Instagram className="h-4 w-4" strokeWidth={1.5} />
            </a>
          ) : null}
          <a
            href="#contact"
            data-testid="nav-cta-book"
            className={`hidden md:inline-flex items-center h-10 px-5 text-[11px] uppercase tracking-[0.22em] font-medium transition-colors ${
              dark ? "bg-ivory text-charcoal hover:bg-gold hover:text-ivory" : "bg-teal-600 text-ivory hover:bg-charcoal"
            }`}
          >
            Book a Consultation
          </a>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className={`lg:hidden inline-flex items-center justify-center h-10 w-10 border ${dark ? "border-ivory/40 text-ivory" : "border-charcoal/25 text-charcoal"}`}
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 bg-ivory border-b border-beige ${open ? "max-h-[520px]" : "max-h-0"}`}
        data-testid="nav-mobile-menu"
      >
        <div className="px-5 py-6 flex flex-col gap-5">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${s.id}`}
              className="text-sm uppercase tracking-[0.22em] text-charcoal"
            >
              {s.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-cta"
            className="mt-2 inline-flex w-fit items-center h-11 px-5 text-[11px] uppercase tracking-[0.22em] bg-teal-600 text-ivory"
          >
            Book a Consultation
          </a>
          <Link
            to="/admin/login"
            data-testid="nav-mobile-admin"
            className="mt-1 text-[10px] uppercase tracking-[0.3em] text-charcoal/40"
            onClick={() => setOpen(false)}
          >
            Admin
          </Link>
        </div>
      </div>
    </header>
  );
}
